/**
 * HealthBanner — banner global visible cuando hay drift de salud del sistema.
 *
 * Polls /api/admin/health-overview cada 5 min. Si alguna sección está en yellow
 * o red, muestra una barra en el tope de la página con el motivo y un link
 * al panel completo /admin/health.
 *
 * El estado "dismiss" es per-session (sessionStorage) y solo oculta hasta el
 * próximo cambio de overall — si el drift escala (yellow → red), reaparece.
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

type Light = 'green' | 'yellow' | 'red';

interface SectionData {
  status: Light;
  reason?: string;
  data?: unknown;
}

interface HealthOverviewResp {
  generated_at: string;
  overall: Light;
  sections: Record<string, SectionData>;
}

const SECTION_LABELS: Record<string, string> = {
  pm2: 'PM2',
  processes_running: 'Procesos',
  queues: 'Queues',
  disk: 'Disco',
  pg_backup: 'Copia de seguridad DB',
  errors_recent: 'Errores recientes',
  ml_tokens: 'Tokens ML',
  gitnexus: 'GitNexus',
  mcp_servers: 'MCP',
  item_sources_coverage: 'Cobertura PM',
  up_link_coverage: 'Cobertura PM UP',
  ml_truth_desync: 'Desync ML',
  amazon_cancelled_purchases: 'Amazon canceló compra',
  price_monitor: 'Monitor de Precios',
};

const POLL_MS = 5 * 60_000;
const DISMISS_KEY = 'health_banner_dismissed_overall';

export function HealthBanner() {
  const [overview, setOverview] = useState<HealthOverviewResp | null>(null);
  const [dismissed, setDismissed] = useState<string | null>(null);

  useEffect(() => {
    setDismissed(sessionStorage.getItem(DISMISS_KEY));
  }, []);

  useEffect(() => {
    let aborted = false;
    async function fetchHealth() {
      try {
        const res = await fetch('/api/admin/health-overview', {
          credentials: 'include',
        });
        if (!res.ok) return;
        const json = (await res.json()) as { success: boolean; data: HealthOverviewResp | null };
        if (!aborted) setOverview(json.data ?? null);
      } catch {
        // Silencioso — el banner no es crítico, no espamear console
      }
    }
    fetchHealth();
    const id = setInterval(fetchHealth, POLL_MS);
    return () => {
      aborted = true;
      clearInterval(id);
    };
  }, []);

  if (!overview || !overview.sections) return null;
  if (overview.overall === 'green') return null;

  // Permite ocultar PERO si overall escala (yellow → red), reaparece
  const currentSignature = `${overview.overall}:${Object.entries(overview.sections)
    .filter(([, s]) => s.status !== 'green')
    .map(([k, s]) => `${k}=${s.status}`)
    .join(',')}`;
  if (dismissed === currentSignature) return null;

  const problems = Object.entries(overview.sections)
    .filter(([, s]) => s.status !== 'green')
    .map(([k, s]) => ({
      key: k,
      label: SECTION_LABELS[k] || k,
      status: s.status,
      reason: s.reason,
    }));

  const bgClass =
    overview.overall === 'red'
      ? 'bg-red-100 border-red-400 text-red-900'
      : 'bg-yellow-100 border-yellow-400 text-yellow-900';

  const dismiss = () => {
    sessionStorage.setItem(DISMISS_KEY, currentSignature);
    setDismissed(currentSignature);
  };

  return (
    <div
      className={`border-l-4 ${bgClass} px-4 py-2 mb-3 flex items-start justify-between gap-3 rounded shadow-sm`}
      role="alert"
    >
      <div className="flex-1 min-w-0">
        <div className="font-semibold flex items-center gap-2 mb-1">
          <span aria-hidden="true">{overview.overall === 'red' ? '🔴' : '🟡'}</span>
          Atención: estado del sistema en {overview.overall === 'red' ? 'rojo' : 'amarillo'}
        </div>
        <ul className="text-sm space-y-0.5">
          {problems.slice(0, 4).map((p) => (
            <li key={p.key} className="truncate">
              <span className="font-medium">{p.label}:</span>{' '}
              {p.reason || `estado ${p.status}`}
            </li>
          ))}
          {problems.length > 4 && (
            <li className="italic">… y {problems.length - 4} más</li>
          )}
        </ul>
        <div className="mt-1.5 text-sm">
          <Link
            to="/admin/health"
            className="underline font-medium hover:no-underline"
          >
            Ver panel completo →
          </Link>
        </div>
      </div>
      <button
        type="button"
        onClick={dismiss}
        aria-label="Ocultar aviso hasta que cambie"
        className="text-lg leading-none px-2 py-0.5 hover:bg-black/5 rounded"
        title="Ocultar hasta que cambie el estado"
      >
        ×
      </button>
    </div>
  );
}
